import React, { useContext, useMemo, useRef } from 'react';
import { defineMessages } from 'react-intl';
import type { IntlShape } from 'react-intl';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import VideoIcon from '@atlaskit/icon/core/video';
import { Inline } from '@atlaskit/primitives/compiled/inline';

import { ConfluenceExperimentsContext } from '@post-office/growth-common/confluence-experiments-context';

import type {
	FlagDescriptor,
	FlagsStateContainer,
} from '@confluence/flags/entry-points/FlagsStateContainer.ts';

import { PartyIcon } from '../assets/PartyIcon.tsx';

const TEMPLATE_XFLOW_SOURCE_COMPONENT = 'twcg2112ConfluenceTemplateXflow';
const TEMPLATE_XFLOW_EXPERIMENT_KEY = 'twcg-2112-twc-xflow-conf-pages-templates';

export type TemplateXflowSuccessFlagSource = 'publishSuccessFlag' | 'shareSuccessFlag';

export type TemplateXflowSuccessFlag = {
	blueprintModuleCompleteKey: string;
	onDismiss: () => void;
	onTryItFree: () => void;
};

type UseTemplateXflowSuccessFlagProps = {
	cloudId: string | null | undefined;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	environment: string | null | undefined;
	orgId: string | null | undefined;
	source: TemplateXflowSuccessFlagSource;
};

const i18n = defineMessages({
	templateXflowSuccessDescription: {
		id: 'share-and-restrict-dialog.template-xflow.flags.description',
		defaultMessage:
			'Skip the manual notes next time. Record your meeting and AI will write one for you.',
		description: 'Description of a share success flag promoting AI meeting notes',
	},
	templateXflowTryItFree: {
		id: 'share-and-restrict-dialog.template-xflow.flags.try-it-free',
		defaultMessage: 'Try it free',
		description: 'Action to try AI meeting notes from the share success flag',
	},
	templateXflowRemindMeLater: {
		id: 'share-and-restrict-dialog.template-xflow.flags.remind-me-later.ai-non-final',
		defaultMessage: 'Remind me later',
		description:
			'Action on the share success flag that dismisses the AI meeting notes promotion for now',
	},
});

const getTemplateXflowModalUrl = ({
	cloudId,
	environment,
	orgId,
	source,
}: {
	cloudId: string;
	environment: string | null | undefined;
	orgId: string;
	source: TemplateXflowSuccessFlagSource;
}) => {
	const url = new URL(
		`https://home${environment === 'PRODUCTION' ? '' : '.stg'}.atlassian.com/o/${orgId}/`,
	);
	url.searchParams.set('cloudId', cloudId);
	url.searchParams.set('openTwcXflowModal', 'true');
	url.searchParams.set('source', TEMPLATE_XFLOW_SOURCE_COMPONENT);
	url.searchParams.set('context', source);
	return url.toString();
};

export const useTemplateXflowSuccessFlag = ({
	cloudId,
	createAnalyticsEvent,
	environment,
	orgId,
	source,
}: UseTemplateXflowSuccessFlagProps): TemplateXflowSuccessFlag | undefined => {
	const { templateXflow } = useContext(ConfluenceExperimentsContext);
	const hasEmittedTerminalEvent = useRef(false);
	const blueprintModuleCompleteKey =
		templateXflow?.currentPageTemplateData.blueprintModuleCompleteKey;

	return useMemo(() => {
		if (
			templateXflow?.currentPageTemplateData.isEligible !== true ||
			templateXflow.reminderState !== 'remind' ||
			templateXflow.isTerminallyDismissed ||
			!blueprintModuleCompleteKey ||
			!cloudId ||
			!orgId
		) {
			return undefined;
		}

		const dismissForSession = (sourceAction: 'tryItFree' | 'explicitDismiss') => {
			if (hasEmittedTerminalEvent.current) {
				return;
			}

			hasEmittedTerminalEvent.current = true;
			templateXflow.dismissForSession();
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'changed',
					actionSubject: 'twcTemplateXflowReminder',
					attributes: {
						blueprintModuleCompleteKey,
						experimentKey: TEMPLATE_XFLOW_EXPERIMENT_KEY,
						reminderState: 'dismissed',
						source,
						sourceAction,
					},
				},
			}).fire();
		};

		return {
			blueprintModuleCompleteKey,
			onDismiss: () => dismissForSession('explicitDismiss'),
			onTryItFree: () => {
				dismissForSession('tryItFree');
				window.location.assign(
					getTemplateXflowModalUrl({
						cloudId,
						environment,
						orgId,
						source,
					}),
				);
			},
		};
	}, [
		blueprintModuleCompleteKey,
		cloudId,
		createAnalyticsEvent,
		environment,
		orgId,
		source,
		templateXflow,
	]);
};

type ShowTemplateXflowSuccessFlagProps = {
	createAnalyticsEvent?: CreateUIAnalyticsEvent;
	extraFlagProps?: Pick<FlagDescriptor, 'close' | 'messageId' | 'messageType'>;
	flags: FlagsStateContainer;
	intl: IntlShape;
	source: TemplateXflowSuccessFlagSource;
	templateXflowFlag: TemplateXflowSuccessFlag;
	timeoutMs?: number;
	title: string;
};

export const showTemplateXflowSuccessFlag = async ({
	createAnalyticsEvent,
	extraFlagProps,
	flags,
	intl,
	source,
	templateXflowFlag,
	timeoutMs,
	title,
}: ShowTemplateXflowSuccessFlagProps): Promise<void> => {
	let flagId: string | number | undefined;
	let hasInteraction = false;
	const actionSubject =
		source === 'publishSuccessFlag'
			? 'templateXflowPublishSuccessFlag'
			: 'templateXflowShareSuccessFlag';
	const fireTemplateXflowEvent = (
		type: 'sendUIEvent' | 'sendOperationalEvent',
		action: 'clicked' | 'dismissed' | 'timedOut' | 'viewed',
		actionSubjectId: string,
	) =>
		createAnalyticsEvent?.({
			type,
			data: {
				action,
				actionSubject,
				actionSubjectId,
				source,
				attributes: {
					blueprintModuleCompleteKey: templateXflowFlag.blueprintModuleCompleteKey,
				},
			},
		}).fire();

	const flag = await flags.showFlag({
		...extraFlagProps,
		type: 'custom',
		customIcon: <PartyIcon />,
		title,
		description: intl.formatMessage(i18n.templateXflowSuccessDescription),
		actions: [
			{
				content: (
					<Inline alignBlock="center" space="space.050">
						<VideoIcon label="" />
						{intl.formatMessage(i18n.templateXflowTryItFree)}
					</Inline>
				),
				onClick: () => {
					if (hasInteraction) {
						return;
					}
					hasInteraction = true;
					fireTemplateXflowEvent('sendUIEvent', 'clicked', 'tryItFree');
					templateXflowFlag.onTryItFree();
				},
			},
			{
				content: intl.formatMessage(i18n.templateXflowRemindMeLater),
				onClick: () => {
					if (hasInteraction) {
						return;
					}
					hasInteraction = true;
					fireTemplateXflowEvent('sendUIEvent', 'clicked', 'remindMeLater');
					void flags.hideFlag(flagId);
				},
			},
		],
		onClose: () => {
			if (hasInteraction) {
				return;
			}
			hasInteraction = true;
			fireTemplateXflowEvent('sendUIEvent', 'dismissed', 'explicitClose');
			templateXflowFlag.onDismiss();
		},
	});

	flagId = flag.id;
	fireTemplateXflowEvent('sendUIEvent', 'viewed', 'flag');
	if (timeoutMs !== undefined) {
		setTimeout(() => {
			if (!hasInteraction) {
				fireTemplateXflowEvent('sendOperationalEvent', 'timedOut', 'autoDismiss');
				hasInteraction = true;
			}
			void flags.hideFlag(flag.id);
		}, timeoutMs);
	}
};
